'use client';
// src/pages/admin/ConsentAuditLog.jsx
//
// Founder-only browser for the TCPA / 10DLC consent audit log. Reads
// directly from `consentEvents/` (top-level collection) — the firestore
// rule on that collection already gates to `isFounder()`, so this page
// renders empty for anyone else even if they navigate to the URL.
//
// Indexes / scale:
//   This page fetches the latest N events (default 200) and filters on
//   the client. Fine for the current scale. If consentEvents grows past
//   ~10k docs it's worth adding composite indexes (phoneNumber+timestamp,
//   uid+timestamp, action+timestamp) and moving to server-side filters.
//
// Each row is one immutable record of a grant or revoke event with full
// captured context (IP, UA, source page, exact disclosure text version,
// identity snapshot). Used for:
//   - TCPA defense if a parent disputes consent
//   - Carrier audit during 10DLC review
//   - Internal compliance spot-checks

import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
  ClockIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const DEFAULT_LIMIT = 200;

function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
}

function fmtTimestamp(v) {
  const d = toDate(v);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

function ActionPill({ action }) {
  const isGrant = action === 'granted';
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide',
        isGrant
          ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200'
          : 'bg-red-100 text-red-700 ring-1 ring-red-200'
      )}
      style={{ fontFamily: HEAD_FONT }}
    >
      {isGrant
        ? <CheckCircleIcon className="h-3 w-3" />
        : <XCircleIcon className="h-3 w-3" />}
      {action}
    </span>
  );
}

function EventRow({ event }) {
  const [expanded, setExpanded] = useState(false);
  const phone = event.phoneNumber || '—';
  const identity = event.identitySnapshot || {};
  const idLabel = identity.email || identity.displayName || event.uid || 'unknown';

  return (
    <li className="border-b border-zinc-200 dark:border-zinc-700 last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <ActionPill action={event.action} />
          <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 flex-1 min-w-0 truncate">
            {idLabel}
          </span>
          <span className="text-xs text-zinc-500 font-mono">{phone}</span>
          <span className="text-xs text-zinc-400 inline-flex items-center gap-1">
            <ClockIcon className="h-3 w-3" />
            {fmtTimestamp(event.timestamp)}
          </span>
          {expanded
            ? <ChevronUpIcon className="h-4 w-4 text-zinc-400" />
            : <ChevronDownIcon className="h-4 w-4 text-zinc-400" />}
        </div>
        <div className="mt-1.5 text-xs text-zinc-500 truncate">
          source: <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1 py-0.5">{event.source || '—'}</code>
          {event.consentTextVersion && (
            <> · version: <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1 py-0.5">{event.consentTextVersion}</code></>
          )}
          {event.uid && (
            <> · uid: <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1 py-0.5 font-mono">{event.uid.slice(0, 12)}…</code></>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs bg-zinc-50/50 dark:bg-zinc-800/30">
          <Field label="UID" value={event.uid} mono />
          <Field label="Type" value={event.type} />
          <Field label="Verification channel" value={event.verificationChannel} />
          <Field label="IP address" value={event.ipAddress} mono />
          <Field label="Display name" value={identity.displayName} />
          <Field label="Email" value={identity.email} />
          <Field label="User agent" value={event.userAgent} mono className="sm:col-span-2 break-all" />
          {event.consentText && (
            <Field
              label="Disclosure text shown"
              value={event.consentText}
              className="sm:col-span-2 whitespace-pre-wrap"
            />
          )}
          {event.extras && Object.keys(event.extras).length > 0 && (
            <Field
              label="Extras"
              value={JSON.stringify(event.extras, null, 2)}
              mono
              className="sm:col-span-2 whitespace-pre-wrap"
            />
          )}
        </div>
      )}
    </li>
  );
}

function Field({ label, value, mono = false, className = '' }) {
  if (value == null || value === '') value = '—';
  return (
    <div className={className}>
      <p className="text-[10px] uppercase font-bold tracking-wider text-zinc-400 mb-0.5">{label}</p>
      <p className={clsx('text-zinc-700 dark:text-zinc-300', mono && 'font-mono text-[11px]')}>
        {String(value)}
      </p>
    </div>
  );
}

export default function ConsentAuditLog() {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');
  const [actionFilter, setActionFilter] = useState('all'); // 'all' | 'granted' | 'revoked'

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(query(
        collection(db, 'consentEvents'),
        orderBy('timestamp', 'desc'),
        limit(DEFAULT_LIMIT),
      ));
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('[ConsentAuditLog] fetch failed', err);
      setError(err?.message || 'Failed to load events. Founder claim required.');
      toast.error('Could not load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (actionFilter !== 'all' && e.action !== actionFilter) return false;
      if (!q) return true;
      const haystack = [
        e.phoneNumber,
        e.uid,
        e.identitySnapshot?.email,
        e.identitySnapshot?.displayName,
        e.source,
        e.ipAddress,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [events, search, actionFilter]);

  const counts = useMemo(() => {
    const granted = events.filter((e) => e.action === 'granted').length;
    const revoked = events.filter((e) => e.action === 'revoked').length;
    return { granted, revoked };
  }, [events]);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 p-6 sm:p-8">
      <div className="mx-auto max-w-4xl space-y-6">

        {/* Header */}
        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500"
              style={{ fontFamily: HEAD_FONT }}
            >
              Admin · Compliance
            </p>
            <h1
              className="mt-1 text-2xl font-bold uppercase tracking-tight text-zinc-900 dark:text-white"
              style={{ fontFamily: HEAD_FONT }}
            >
              SMS Consent Audit Log
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Append-only TCPA / 10DLC trail. Latest {DEFAULT_LIMIT} events.{' '}
              <span className="text-emerald-600 font-semibold">{counts.granted} grants</span>{' · '}
              <span className="text-red-600 font-semibold">{counts.revoked} revokes</span>
            </p>
          </div>
          <button
            type="button"
            onClick={fetchEvents}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-4 py-2 text-sm font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={clsx('h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </button>
        </header>

        {/* Filters */}
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 shadow-sm p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by phone, email, UID, source, IP…"
              className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 pl-9 pr-4 py-2 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden flex-shrink-0">
            {[
              { value: 'all',     label: 'All' },
              { value: 'granted', label: 'Grants' },
              { value: 'revoked', label: 'Revokes' },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setActionFilter(opt.value)}
                className={clsx(
                  'px-3 py-2 text-xs font-semibold transition-colors',
                  actionFilter === opt.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 rounded-xl bg-zinc-200 dark:bg-zinc-800 animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 p-6 text-center">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300">{error}</p>
            <p className="text-xs text-zinc-500 mt-2">
              Need the <code className="rounded bg-zinc-200 dark:bg-zinc-700 px-1">founder</code> claim.
              Use the Platform Claims page to grant it, or run{' '}
              <code className="rounded bg-zinc-200 dark:bg-zinc-700 px-1">node scripts/grant-founder.mjs &lt;UID&gt;</code>.
            </p>
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-10 text-center">
            <ShieldCheckIcon className="mx-auto h-10 w-10 text-zinc-300 mb-3" />
            <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">No events yet</p>
            <p className="text-xs text-zinc-500 mt-1">
              Consent records appear here as users opt in or out of SMS.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-10 text-center">
            <p className="text-sm text-zinc-500">No events match your filter.</p>
            <p className="text-xs text-zinc-400 mt-1">
              Showing 0 of {events.length} loaded events.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
            <ul>
              {filtered.map((e) => <EventRow key={e.id} event={e} />)}
            </ul>
            {filtered.length < events.length && (
              <div className="border-t border-zinc-200 dark:border-zinc-700 px-4 py-3 text-xs text-zinc-400 text-center">
                Showing {filtered.length} of {events.length} loaded events.
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
